import loadable from '@loadable/component';
import { useState } from 'react';

import { Box, Tab, TabList, TabPanel } from '@simplywallst/ui-core';
import { CompanyNoteModalProvider } from '@/components/Note';
import { NarrativeValuator } from '../NarrativeValuator';

import { NarrativeForm } from '../../utils/schema';
import { NarrativeCompany } from '../../hooks/useNarrativeById';

const CompanyInfo = loadable(
  () => import('@/pages/Narrative/components/CompanyInfo')
);
const Notes = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Notes,
});

type TabPanelProps = 'valuator' | 'companyInfo' | 'notes';

type Props = {
  company: NarrativeCompany;
  onChange: (values: Partial<NarrativeForm>) => void;
  readOnly: boolean;
  values: NarrativeForm;
  authorDisplayName: string;
  onReset: () => void;
};

export const NarrativeTabs = ({
  authorDisplayName,
  company,
  onChange,
  readOnly,
  values,
  onReset,
}: Props) => {
  const [activeTab, setActiveTab] = useState<TabPanelProps>('valuator');
  return (
    <CompanyNoteModalProvider>
      <TabList
        boxShadowBottom="subtleBorder"
        templateRows="min-content"
        templateColumns={`repeat(3, max-content)`}
        paddingX="x2"
        justifyContent="flex-start"
      >
        <Tab<TabPanelProps>
          styleType="subnav"
          hasPanel
          selected={activeTab === 'valuator'}
          key="valuator"
          value="valuator"
          onClick={() => setActiveTab('valuator')}
        >
          Valuator
        </Tab>
        <Tab<TabPanelProps>
          styleType="subnav"
          hasPanel
          selected={activeTab === 'companyInfo'}
          key="companyInfo"
          value="companyInfo"
          onClick={() => setActiveTab('companyInfo')}
        >
          Company Info
        </Tab>
        <Tab<TabPanelProps>
          styleType="subnav"
          hasPanel
          selected={activeTab === 'notes'}
          key="notes"
          value="notes"
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </Tab>
      </TabList>
      <Box overflow="hidden">
        <TabPanel
          selected={activeTab === 'valuator'}
          value="valuator"
          padding="x2"
        >
          {company && (
            <NarrativeValuator
              company={company}
              values={values}
              onChange={onChange}
              readOnly={readOnly}
              authorDisplayName={authorDisplayName}
              onReset={onReset}
            />
          )}
        </TabPanel>
        <TabPanel
          selected={activeTab === 'companyInfo'}
          value="companyInfo"
          paddingLg="x2"
        >
          {company && <CompanyInfo company={company} />}
        </TabPanel>
        <TabPanel
          selected={activeTab === 'notes'}
          value="notes"
          padding="x2spacePx"
        >
          {company && (
            <Notes uniqueSymbol={company.uniqueSymbol} companyId={company.id} />
          )}
        </TabPanel>
      </Box>
    </CompanyNoteModalProvider>
  );
};
